import {
	logOutLocal,
	updateTokens,
	selectRefreshToken,
	selectAccountMembershipId,
} from '@shared/api/reduxToolkit';

import { baseQuery, rawBaseQuery } from './baseQuery';
import { BASE_QUERY_CONFIG } from './baseQueryConfig';
import { getRefreshPromise, setRefreshPromise, clearRefreshPromise } from '../refresh/refreshState';

export const baseQueryWithReauth = async (args, api, extraOptions) => {
	const result = await baseQuery(args, api, extraOptions);

	const status = result.error?.status;

	// Hard logout on 419 — no refresh attempt. 403 is a permission error, returned to the caller.
	if (BASE_QUERY_CONFIG.logoutOnErrorCodes.includes(status)) {
		api.dispatch(logOutLocal());
		return result;
	}

	if (status !== 401) {
		return result;
	}

	// --- 401 path: attempt silent token refresh ---
	const refreshToken = selectRefreshToken(api.getState());
	const userId = selectAccountMembershipId(api.getState());

	// No refresh token — force logout immediately
	if (!refreshToken) {
		api.dispatch(logOutLocal());
		return result;
	}

	// Mutex: if another request already started a refresh, wait on the same promise
	let pendingRefresh = getRefreshPromise();

	if (!pendingRefresh) {
		// We are the first — start the refresh
		const promise = rawBaseQuery(
			{
				url: '/v1/user/refreshToken',
				method: 'POST',
				body: { refreshToken, userId },
			},
			api,
			extraOptions,
		)
			.then((refreshResult) => {
				if (refreshResult.data) {
					api.dispatch(updateTokens(refreshResult.data));
					return true;
				}
				api.dispatch(logOutLocal());
				return false;
			})
			.catch(() => {
				api.dispatch(logOutLocal());
				return false;
			})
			.finally(() => {
				clearRefreshPromise();
			});

		setRefreshPromise(promise);
		pendingRefresh = promise;
	}

	const refreshed = await pendingRefresh;

	if (!refreshed) {
		return result;
	}

	// Refresh succeeded — retry the original request with the new access token
	// baseQuery will pick up the new token from Redux state via prepareHeaders
	return baseQuery(args, api, extraOptions);
};
